import {LoadingOutlined} from '@ant-design/icons';
import {Checkbox, message, Spin} from 'antd';
import {isEmpty, omit} from 'lodash-es';
import React, {
    FC,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    getValidationResult,
    getRequestValidationFields,
    EDITABLE_TABLE_LIST_NAME,
    EditableFormTableContext,
} from 'components/editable-table';
import {selectTableEntityMetadata} from 'entities/metadata';
import {selectContextRawData} from 'modules/context/context-selector';
import {TEntity} from 'modules/data';
import {saveMultipleEditableData, setDataLoading} from 'modules/data/data-actions';
import {selectTableEntityData} from 'modules/data/data-selectors';
import {EEntityType} from 'shared/constants/entities';
import {ERequestType} from 'shared/types/requests';
import {areArraysEqual, getChangedFields} from 'shared/utils/arrays';
import {
    AppControlProceduresResultApi,
    useGetIsAppStatisticsConfirmedQuery,
    useLazyGetAppStatisticsConfirmedQuery,
} from 'store/api/app-control-procedures-result/app-control-procedures-result.api';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import {ITableActionProps} from '../table-action-types';

import './button-action-save-editable-table.less';

export const ButtonActionSaveEditableTable: FC<ITableActionProps> = ({
    meta,
    entityName,
}: ITableActionProps) => {
    const dispatch = useAppDispatch();
    const [value, setValue] = useState<boolean>(false);
    const [oldData, setOldData] = useState<TEntity[]>();
    const {form, validation: validationContext} = useContext(EditableFormTableContext);
    const context = useAppSelector(selectContextRawData);
    const tableMeta = useAppSelector(selectTableEntityMetadata(entityName));
    const tableData = useAppSelector(selectTableEntityData(entityName));
    const requestValidationFields = getRequestValidationFields(tableMeta?.fields);
    const editTableData = form?.getFieldsValue()[EDITABLE_TABLE_LIST_NAME];

    const dataToSend = useMemo(() => (
        getChangedFields(tableData.initialRows || [], editTableData || [])
    ), [editTableData]);

    const {data: isStatisticsConfirmed, isFetching} = useGetIsAppStatisticsConfirmedQuery(
        {entityName, params: context}, {skip: !entityName, refetchOnMountOrArgChange: true},
    );
    const [triggerConfirmStatisticsQuery] = useLazyGetAppStatisticsConfirmedQuery();

    const {
        validateAllRows,
        getAllFieldErrors,
    } = validationContext || {};

    useEffect(() => {
        if (dataToSend?.length && !areArraysEqual(dataToSend, oldData)) {
            setValue(false);
        }
        else setValue(!!isStatisticsConfirmed);
    }, [dataToSend, isStatisticsConfirmed]);

    const handleClick = async () => {
        if (form) {
            const areAllFieldsVisible = dataToSend?.length === tableData?.params?.paginationTotal;
            let isError = false;
            let isOtherPageError = false;
            const invalidFormIds: number[] = [];
            const errorCPCodes = new Set<string>();
            const currentEditTableData = form?.getFieldsValue()[EDITABLE_TABLE_LIST_NAME];

            // Валидация полей в таблице
            if (validateAllRows) await validateAllRows(currentEditTableData);
            const errors: Record<string, string[]>[] = getAllFieldErrors ? getAllFieldErrors() : [];
            errors.forEach((fieldError: object, index: number) => {
                if (
                    Object.keys(fieldError).length && currentEditTableData[index]
                ) invalidFormIds.push(currentEditTableData[index].id);
            });
            if (
                errors?.length && errors?.filter(error => error && Object.keys(error).length > 0)?.length
            ) isError = true;

            // Валидация полей по запросу
            if (requestValidationFields?.length && !areAllFieldsVisible) {
                const loadingMessage = message.loading('Происходит валидация данных', 0);
                dispatch(setDataLoading({
                    entityName,
                    loading: true,
                }));

                await Promise.all(requestValidationFields.map(async item => {
                    try {
                        if (item?.referenceUrl && item?.fieldKey) {
                            const {referenceUrl, fieldKey} = item;
                            const errorFields: {id: number; codeCp: string}[] = await getValidationResult(
                                referenceUrl,
                                fieldKey,
                                context,
                            );

                            if (fieldKey && errorFields) {
                                // Фильтруем ID, оставляя только те, которых нет в dataToSend
                                const invisibleIds = errorFields.filter(errorField =>
                                    !dataToSend?.some((formData: Record<string, any>) =>
                                        formData && formData.id === errorField.id
                                    )
                                );

                                // Если есть ID, значит есть ошибки в данных на других страницах
                                if (invisibleIds.length > 0) {
                                    isOtherPageError = true;
                                    errorFields.forEach(errorField => {
                                        errorCPCodes.add(errorField.codeCp);
                                    })
                                }
                            }
                        }
                    } catch (e) {
                        isOtherPageError = true;
                        console.error(e);
                    } finally {
                        dispatch(setDataLoading({
                            entityName,
                            loading: false,
                        }));
                        loadingMessage();
                    }
                }));
            }

            // Сохранение
            const formsToSave: TEntity[] = currentEditTableData.map((item: TEntity) => {
                const id = item.id as number;
                const formData = omit(dataToSend?.find((data: Record<string, any>) => data?.id === id), 'files');
                const isFormValid = !invalidFormIds.includes(id);

                if (isFormValid && !isEmpty(formData) && !invalidFormIds.includes(id)) {
                    const processedFormData = {...formData};

                    // Присваиваем ноль пустым необязательным полям INPUT_NUMBER
                    tableMeta?.fields?.forEach(field => {
                        if (field.inputType === 'INPUT_NUMBER' && !field.isRequired
                            && (formData[field.key] === '' || formData[field.key] === null ||
                                formData[field.key] === undefined)) {
                            processedFormData[field.key] = 0;
                            item[field.key] = 0;
                        }
                    });

                    return processedFormData;
                }
                return {};
            }).filter((formToSave: TEntity) => Object.keys(formToSave)?.length);

            try {
                if (formsToSave.length && meta.referenceUrl) {
                    await dispatch(saveMultipleEditableData(
                        entityName,
                        EEntityType.TABLE,
                        ERequestType.POST,
                        formsToSave,
                        meta.referenceUrl,
                        true,
                        true,
                        true,
                    ));
                    setOldData(formsToSave);
                }
            } catch (e) {
                isError = true;
                console.error(e);
            }

            if (!isError && !isOtherPageError) {
                await triggerConfirmStatisticsQuery({entityName, params: context}).unwrap();
                await dispatch(AppControlProceduresResultApi.util.invalidateTags(['AppStatisticsConfirmed']));
                message.success('Статистика и описание КП успешно подтверждены');
                setValue(isStatisticsConfirmed);
            } else {
                setValue(false);
                if (errorCPCodes.size) {
                    message.error(`Для КП ${Array.from(errorCPCodes).join(', ')} поля заполнены неверно`);
                    return;
                }
                message.error(isOtherPageError ? 'Заполните все поля корректно на всех страницах' : 'Заполните все поля корректно');
            }
        }
    };

    return (
        isFetching ? (
            <Spin
                className="button-save-editable-spin d-flex justify-content-center"
                indicator={<LoadingOutlined spin />}
                size="small"
                tip={meta.actionTitle}
            />
        ) : (
            <Checkbox
                onClick={handleClick}
                checked={value}
                value={value}
                disabled={value}
            >
                {meta.actionTitle}
            </Checkbox>
        )
    );
};
